/**
 * Read-only futures picks display for the member profile page.
 */

import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { Image } from 'expo-image';
import { Globe, Lock } from 'lucide-react-native';
import { getFlagCdnUrl } from '../../../src/worldcup/helpers/flagCdn.helper';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import { resolveLeaderboardFuturesDisplay } from '../PicksView/futuresCardDisplay.helper';
import { SoccerKit } from '../../ui/SoccerKit';
import { SettledIcon } from './SettledIcon';
import { MEMBER_PROFILE_COPY, MEMBER_PROFILE_TEST_IDS } from './MemberProfileView.constants';
import type { WorldCupFuturesOptions } from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import type { MemberProfileFutures } from '../../../src/worldcup/helpers/worldCupMemberProfileApi.helper';
import type { getMemberProfileViewStyles } from './MemberProfileView.styles';
import type { Theme } from '../../../src/theme/getTheme';

interface MemberFuturesDisplayProps {
  futures: MemberProfileFutures | null;
  /** When true, picks are withheld until the first match kicks off — render locked. */
  futuresHidden?: boolean;
  futuresOptions: WorldCupFuturesOptions | null;
  /**
   * Flags (national teams) or kits (clubs). Defaults to flags, i.e. the World Cup, whose
   * winner IS a nation and whose square is therefore already correct (AC-15.16).
   */
  teamVisual?: 'flag' | 'kit';
  styles: ReturnType<typeof getMemberProfileViewStyles>;
  theme: Theme;
}

export const MemberFuturesDisplay: React.FC<MemberFuturesDisplayProps> = ({
  futures,
  futuresHidden = false,
  futuresOptions,
  teamVisual = 'flag',
  styles,
  theme,
}) => {
  const resolved = useMemo(
    () => resolveLeaderboardFuturesDisplay(
      futures?.winnerTeamId,
      futures?.topScorerPlayerId,
      futuresOptions,
    ),
    [futures?.winnerTeamId, futures?.topScorerPlayerId, futuresOptions],
  );

  const winnerFlagUri = resolved.winnerCountryCode ? getFlagCdnUrl(resolved.winnerCountryCode) : null;
  const scorerFlagUri = resolved.scorerKitMeta?.countryCode
    ? getFlagCdnUrl(resolved.scorerKitMeta.countryCode)
    : null;
  const winnerLabel = resolved.winnerCaption ?? 'Not picked';
  // AC-15.16: the Winner square drew a bare flag while the Top Scorer beside it drew a kit on
  // its flag — the asymmetry AC-15.11 already removed from the picks screen. A club's kit is
  // its identity; a nation's flag is, so `teamVisual` keeps the World Cup as it was.
  const winnerKit = useMemo(
    () => (teamVisual === 'kit' && resolved.winnerCaption
      ? getTeamKitConfig(resolved.winnerCaption)
      : null),
    [teamVisual, resolved.winnerCaption],
  );
  const scorerLabel = resolved.scorerCaption ?? 'Not picked';

  if (futuresHidden) {
    return (
      <View style={styles.futuresRow}>
        {(['Winner', 'Top Scorer'] as const).map((label) => (
          <View key={label} style={styles.futureChip}>
            <Text style={styles.futureChipLabel}>{label}</Text>
            <View style={styles.futureChipMedia}>
              <View style={styles.futureFlagEmpty}>
                <Lock size={18} color={theme.gray[500]} />
              </View>
            </View>
            <View style={styles.futureChipNameRow}>
              <Text style={styles.futureChipName} numberOfLines={1}>
                {MEMBER_PROFILE_COPY.FUTURES_HIDDEN_LABEL}
              </Text>
            </View>
          </View>
        ))}
      </View>
    );
  }

  return (
    <View style={styles.futuresRow}>
      {/* Winner */}
      <View style={styles.futureChip}>
        <Text style={styles.futureChipLabel}>Winner</Text>
        <View style={styles.futureChipMedia}>
          {winnerFlagUri ? (
            <Image
              source={{ uri: winnerFlagUri }}
              style={styles.futureFlag}
              contentFit="cover"
              alt=""
            />
          ) : (
            <View style={styles.futureFlagEmpty}>
              <Globe size={18} color={theme.gray[500]} />
            </View>
          )}
          {winnerKit && (
            <View style={styles.scorerKitOverlay} testID={MEMBER_PROFILE_TEST_IDS.winnerKit}>
              <SoccerKit
                size={32}
                primary={winnerKit.primary}
                secondary={winnerKit.secondary}
                type={winnerKit.type}
              />
            </View>
          )}
        </View>
        <View style={styles.futureChipNameRow}>
          <Text style={styles.futureChipName} numberOfLines={1}>{winnerLabel}</Text>
          <SettledIcon correct={futures?.winnerCorrect ?? null} theme={theme} />
        </View>
      </View>

      {/* Top scorer — flag behind + kit overlay (same as FuturesCard) */}
      <View style={styles.futureChip}>
        <Text style={styles.futureChipLabel}>Top Scorer</Text>
        <View style={styles.futureChipMedia}>
          {scorerFlagUri ? (
            <Image
              source={{ uri: scorerFlagUri }}
              style={styles.futureFlag}
              contentFit="cover"
              alt=""
            />
          ) : (
            <View style={styles.futureFlagEmpty}>
              <Globe size={18} color={theme.gray[500]} />
            </View>
          )}
          {resolved.scorerKitMeta && (
            <View style={styles.scorerKitOverlay}>
              <SoccerKit
                size={32}
                primary={resolved.scorerKitMeta.kitColor}
                secondary={resolved.scorerKitMeta.trimColor ?? theme.gray[400]}
                number={resolved.scorerKitMeta.number}
              />
            </View>
          )}
        </View>
        <View style={styles.futureChipNameRow}>
          <Text style={styles.futureChipName} numberOfLines={1}>{scorerLabel}</Text>
          <SettledIcon correct={futures?.topScorerCorrect ?? null} theme={theme} />
        </View>
      </View>
    </View>
  );
};
